import React, { useState, useEffect } from 'react';
import ModificaAssociato from './ModificaAssociato.js';
import { useParams } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import { Container, Row, Col, Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Alert } from 'reactstrap';
import NuovoPagamento from './NuovoPagamento.js';
import { adalApiFetch } from './adalConfig';
import { DataGrid } from '@mui/x-data-grid';
import moment from 'moment';

moment.locale('it-IT');

const dateFormatter = (params) => {
  return params ? moment(params).format("DD/MM/YYYY") : '';
};

const timeFormatter = (params) => {
  return typeof params === 'string' ? params.slice(11, 16) : '';
};

const colonneVisite = [
  {
    field: 'data',
    headerName: 'Data Visita',
    flex: 1,
    valueFormatter: dateFormatter,
    renderCell: (params) => `${dateFormatter(params.value)}${params.row.ora ? ` ${timeFormatter(params.row.ora)}` : ''}`,
  },
  { field: 'tipo', headerName: 'Tipo', flex: 1 },
  { field: 'stato', headerName: 'Stato', flex: 1 },
  { field: 'codice', headerName: 'Codice prenotazione', flex: 1 },
  { field: 'note', headerName: 'Note', flex: 2 },
];

const colonnePagamenti = [
  { field: 'data', headerName: 'Data Pagamento', flex: 1, valueFormatter: dateFormatter },
  { field: 'modalita', headerName: 'Modalità', flex: 1 },
  { field: 'importo', headerName: 'Importo', flex: 1 },
  { field: 'num_ricevuta', headerName: 'Numero ricevuta', flex: 1 },
  { field: 'codice_voucher', headerName: 'Codice prenotazione', flex: 1, renderCell: (params) => {
      const row = params.row || {};
      // try multiple possible fields for voucher/code
      const voucherCode = row.codice_voucher || null;
      // voucher validity checks
      // determine validity: only consider invalid if a voucher code exists and is marked invalid or expired
      let isValidVoucher = null; // null = missing, true = valid, false = invalid
      if (voucherCode && row.voucher_valido) {
        isValidVoucher = true;
        const scad = row.voucher_data_scadenza || null;
        if (scad && moment(scad).isBefore(moment(), 'day')) isValidVoucher = false;
      } else {
        isValidVoucher = null;
      }

      // style: strike-through for invalid
      const displayText = voucherCode ? voucherCode : '';
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ textDecoration: (isValidVoucher === false) ? 'line-through' : 'none' }}>{displayText}</span>
          {((isValidVoucher === false || isValidVoucher === null)) && (
            <Button size="sm" color="primary" style={{ marginTop: 6 }} onClick={() => {
              // we'll attach handler via DOM event, the parent will implement an event listener
              const ev = new CustomEvent('open-add-voucher', { detail: { id_pagamento: row.id } });
              window.dispatchEvent(ev);
            }}>Aggiungi</Button>
          )}
        </div>
      );
    } },
];

const colonneRichiesteContatto = [
  { field: 'data_richiesta', headerName: 'Data Richiesta', flex: 1, valueFormatter: dateFormatter },
  { field: 'motivo', headerName: 'Motivo', flex: 1 },
  { field: 'stato', headerName: 'Stato', flex: 1 },
  { field: 'esito', headerName: 'Esito', flex: 1},
  { field: 'note', headerName: 'Note', flex: 2,  editable: true },
];

const Visite = ({ idassociato }) => {
  const [visite, setVisite] = useState([]);

  const fetchData = (idassociato) => {
    adalApiFetch(fetch, `${process.env.REACT_APP_BASE_SERVER_URI}/visits/visiteassociato?idassociato=${idassociato}`)
      .then(result => result.json())
      .then(listaVisite => {
        setVisite(Object.values(listaVisite));
      });
  };

  useEffect(() => {
    fetchData(idassociato);
  }, [idassociato]);

  return (
    <DataGrid
      rows={visite}
      columns={colonneVisite}
      autoPageSize
      sortModel={[
        {
          field: 'data',
          sort: 'desc',
        },
      ]}
    />
  );
};

const Pagamenti = ({ idassociato }) => {
  const [pagamenti, setPagamenti] = useState([]);
  const [voucherModalOpen, setVoucherModalOpen] = useState(false);
  const [voucherPaymentId, setVoucherPaymentId] = useState(null);
  const [voucherExpiration, setVoucherExpiration] = useState('');
  const [voucherError, setVoucherError] = useState(null);

  const fetchData = (idassociato) => {
    adalApiFetch(fetch, `${process.env.REACT_APP_BASE_SERVER_URI}/pagamentiassociato?idassociato=${idassociato}`)
      .then(result => result.json())
      .then(listaPagamenti => {
        setPagamenti(Object.values(listaPagamenti));
      });
  };

  useEffect(() => {
    fetchData(idassociato);
  }, [idassociato]);

  useEffect(() => {
    // global listener for Aggiungi button in the grid's renderCell
    const listener = (e) => {
      const data = e.detail || {};
      const idp = data.id_pagamento || (data.row && (data.row.id));
      setVoucherPaymentId(idp);
      setVoucherExpiration('');
      setVoucherError(null);
      setVoucherModalOpen(true);
    };
    window.addEventListener('open-add-voucher', listener);
    return () => window.removeEventListener('open-add-voucher', listener);
  }, []);

  const toggleVoucherModal = () => {
    setVoucherModalOpen(!voucherModalOpen);
    setVoucherError(null);
  };

  const handleAddVoucher = async () => {
    setVoucherError(null);
    if (!voucherPaymentId) return setVoucherError('Pagamento non selezionato');
    if (!voucherExpiration) return setVoucherError('Inserire una data di scadenza');
    try {
      const resp = await adalApiFetch(fetch, `${process.env.REACT_APP_BASE_SERVER_URI}/insvoucher`, {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_pagamento: voucherPaymentId, data_scadenza: voucherExpiration }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => null);
        throw new Error((body && body.error) ? body.error : 'Errore durante l\'inserimento del voucher');
      }
      // refresh pagamenti
      fetchData(idassociato);
      setVoucherModalOpen(false);
    } catch (err) {
      setVoucherError(err.message || 'Errore');
    }
  };

  return (
    <>
      <DataGrid
        rows={pagamenti}
        columns={colonnePagamenti}
        autoPageSize
        sortModel={[
          {
            field: 'data',
            sort: 'desc',
          },
        ]}      
      />

      {/* Modal per aggiungere un voucher a un pagamento */}
      <Modal isOpen={voucherModalOpen} toggle={toggleVoucherModal}>
        <ModalHeader toggle={toggleVoucherModal}>Aggiungi Voucher</ModalHeader>
        <ModalBody>
          {voucherError && <Alert color="danger">{voucherError}</Alert>}
          <FormGroup>
            <Label for="voucherExp">Data scadenza voucher</Label>
            <Input
              id="voucherExp"
              type="date"
              value={voucherExpiration}
              onChange={(e) => setVoucherExpiration(e.target.value)}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleAddVoucher}>Aggiungi</Button>{' '}
          <Button color="secondary" onClick={toggleVoucherModal}>Annulla</Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

const RichiesteContatto = ({ idassociato, refresh }) => {
  const [richiesteContatto, setRichiesteContatto] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  const fetchData = (idassociato) => {
    adalApiFetch(fetch, `${process.env.REACT_APP_BASE_SERVER_URI}/requests/richiestecontattoassociato?id_associato=${idassociato}`)
      .then(result => result.json())
      .then(listaRichieste => {
        setRichiesteContatto(listaRichieste.map(richiesta => ({
          ...richiesta,
          id: richiesta.id_richiestacontatto,
          stato: richiesta.stato === 'A' ? 'Aperta' : richiesta.stato === 'C' ? 'Chiusa' : richiesta.stato
        })));
      })
      .catch(error => {
        setFetchError('Errore durante il recupero delle richieste di contatto.');
        console.error('Errore durante il recupero delle richieste di contatto:', error);
      });
  };

  useEffect(() => {
    fetchData(idassociato);
  }, [idassociato, refresh]);

  const processRowUpdate = async (newRow, oldRow) => {
    setFetchError(null); // resetta errore precedente
    try {
      await adalApiFetch(fetch, `${process.env.REACT_APP_BASE_SERVER_URI}/requests/updatenote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_richiestacontatto: newRow.id, note: newRow.note }),
      });
      // Aggiorna lo stato locale
      setRichiesteContatto((prev) =>
        prev.map((row) => (row.id === newRow.id ? { ...row, note: newRow.note } : row))
      );
      return newRow;
    } catch (error) {
      setFetchError('Errore durante il salvataggio della nota.');
      return oldRow;
    }
  };

  return (
    <>
      {fetchError && <Alert color="danger">{fetchError}</Alert>}
      <DataGrid
        rows={richiesteContatto}
        columns={colonneRichiesteContatto}
        processRowUpdate={processRowUpdate}
        experimentalFeatures={{ newEditingApi: true }}
        autoPageSize
        sortModel={[
          {
            field: 'data_richiesta',
            sort: 'desc',
          },
        ]}
      />
    </>
  );
};

const SchedaAssociato = () => {
  const { id } = useParams();
  const [associato, setAssociato] = useState(null);
  const [modal, setModal] = useState(false);
  const [modalPagamento, setModalPagamento] = useState(false); // Stato per il modale di pagamento
  const [motivo, setMotivo] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState(null);
  const [errorPagamento, setErrorPagamento] = useState(null); // Stato per l'errore del modale di pagamento
  const [alertPagamento, setAlertPagamento] = useState(false); // Stato per l'alert del modale di pagamento
  const [refresh, setRefresh] = useState(false); // Stato per il refresh delle richieste di contatto

  const toggle = () => setModal(!modal);
  const togglePagamento = () => {
    setErrorPagamento(null); // Resetta l'errore quando il modale viene aperto
    setAlertPagamento(false); // Resetta l'alert quando il modale viene aperto
    if (associato && moment(associato.data_scadenza).diff(moment(), 'months') > 10) {
      setAlertPagamento(true);
    } else {
      setModalPagamento(!modalPagamento);
    }
  };

  const fetchData = (idassociato) => {
    adalApiFetch(fetch, `${process.env.REACT_APP_BASE_SERVER_URI}/associato?idassociato=${idassociato}`)
      .then(result => result.json())
      .then(associato => {
        setAssociato(associato);
      });
  };

  useEffect(() => {
    fetchData(id);
  }, [id]);

  let form_na = ' ';

  if (associato) {
    form_na = (
      <ModificaAssociato
        idassociato={id}
        nome={associato.nome}
        cognome={associato.cognome}
        email={associato.email}
        note={associato.note}
        cap={associato.cap}
        luogo_nascita={associato.luogo_nascita}
        indirizzo={associato.indirizzo}
        codice_fiscale={associato.codice_fiscale}
        data_nascita={associato.data_nascita ? moment(associato.data_nascita).format('YYYY-MM-DD') : null}
        telefono={associato.telefono}
        data_scadenza={moment(associato.data_scadenza).format('YYYY-MM-DD')}
        data_iscrizione={moment(associato.data_iscrizione).format('YYYY-MM-DD')}
      />
    );
  }

  const addContact = () => {
    setMotivo('');
    setNote('');
    setError(null);
    toggle();
  };

  const handleSubmit = () => {
    adalApiFetch(fetch, `${process.env.REACT_APP_BASE_SERVER_URI}/requests/insrichiestacontatto`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id_associato: id, motivo, note }),
    })
      .then(response => {
        if (response.ok) {
          toggle();
          setRefresh(!refresh); // Aggiorna lo stato per rifare il fetch delle richieste di contatto
        } else {
          return response.json().then(errorData => {
            setError(`Errore durante il salvataggio: ${errorData.error || 'Errore generico'}`);
          });
        }
      })
      .catch(err => {
        setError(`Errore durante il salvataggio: ${err.message}`);
      });
  };
  
  return (
    <Container fluid="true">
      <Row>
        <Col sm="12" md="6">
          <Row>{form_na}</Row>
        </Col>
        <Col sm="12" md="6">
          <Row style={{ height: '244px', overflowY: 'auto' }}><RichiesteContatto idassociato={id} refresh={refresh} /></Row> {/* Aggiungi il nuovo componente */}
          <Row><p>{' '}</p></Row>
          <Row style={{ height: '242px', overflowY: 'auto' }}><Visite idassociato={id} /></Row>
          <Row><p>{' '}</p></Row>
          <Row style={{ height: '242px', overflowY: 'auto' }}><Pagamenti idassociato={id} /></Row>
        </Col>
      </Row>
      <Row>
        <Col className="d-flex justify-content-center">
          <NavLink className='mt-3 mx-2'>
            <Button color="primary" onClick={addContact}>Contatta</Button>
          </NavLink>
          <NavLink to="/associati" className='mt-3 mx-2'>
            <Button color="danger">Indietro</Button>
          </NavLink>
          <Button color="success" className='mt-3 mx-2' onClick={togglePagamento}>Rinnova</Button> {/* Aggiungi il pulsante Rinnova */}
        </Col>
      </Row>
      <Modal isOpen={modal} toggle={toggle}>
        <ModalHeader toggle={toggle}>Aggiungi richiesta contatto</ModalHeader>
        <ModalBody>
          {error && <Alert color="danger">{error}</Alert>}
          <FormGroup>
            <Label for="motivo">Motivo</Label>
            <Input
              type="select"
              name="motivo"
              id="motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            >
              <option value="">Seleziona un motivo</option>
              <option value="prenotazione">Prenotazione</option>
              <option value="reclamo">Reclamo</option>
              <option value="info">Info</option>
            </Input>
          </FormGroup>
          <FormGroup className="mt-3">
            <Label for="note">Note</Label>
            <Input
              type="text"
              name="note"
              id="note"
              placeholder="Note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSubmit}>Invia</Button>{' '}
          <Button color="secondary" onClick={toggle}>Annulla</Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={modalPagamento || alertPagamento} toggle={() => { setModalPagamento(false); setAlertPagamento(false); }}> {/* Modale per il rinnovo del pagamento */}
        <ModalHeader toggle={() => { setModalPagamento(false); setAlertPagamento(false); }}>Rinnova Pagamento</ModalHeader>
        <ModalBody>
          {alertPagamento ? (
            <Alert color="danger">Impossibile rinnovare: mancano più di 10 mesi alla scadenza</Alert>
          ) : (
            <>
              {errorPagamento && <Alert color="danger">{errorPagamento}</Alert>}
              <NuovoPagamento
                idassociato={id}
                data_scadenza={associato ? moment(associato.data_scadenza).format('YYYY-MM-DD') : ''}
              />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => { setModalPagamento(false); setAlertPagamento(false); }}>Chiudi</Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default SchedaAssociato;